import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageBanner from "@/components/PageBanner";
import SectionHeading from "@/components/SectionHeading";
import heroImage from "@/assets/hero-olive-oil.webp";
import chemlaliImg from "@/assets/chemlali.webp";
import chetouiImg from "@/assets/chetoui.webp";
import customBlendsImg from "@/assets/custom-blends.webp";
import lampanteImg from "@/assets/lampante.webp";

interface ProductSectionProps {
  title: string;
  subheadline: string;
  text: string[];
  image: string;
  profileItems?: string[];
  suitedFor?: string[];
  formats: string;
  reversed?: boolean;
}

const ProductSection = ({ title, subheadline, text, image, profileItems, suitedFor, formats, reversed }: ProductSectionProps) => (
  <section className={`section-padding ${reversed ? "bg-muted" : ""}`}>
    <div className={`container-max grid md:grid-cols-2 gap-12 items-start ${reversed ? "md:[&>*:first-child]:order-2" : ""}`}>
      <div className="rounded-lg overflow-hidden shadow-lg aspect-[4/3]">
        <img loading="lazy" src={image} alt={title} className="w-full h-full object-cover" />
      </div>
      <div>
        <h2 className="font-heading text-3xl font-bold text-foreground mb-2">{title}</h2>
        <p className="text-olive-light font-medium mb-6">{subheadline}</p>
        <div className="space-y-4 text-muted-foreground leading-relaxed mb-6">
          {text.map((t, i) => <p key={i}>{t}</p>)}
        </div>
        {profileItems && (
          <div className="mb-6">
            <h4 className="font-heading text-lg font-semibold text-foreground mb-3">Typical Profile</h4>
            <ul className="space-y-2">
              {profileItems.map((p) => (
                <li key={p} className="text-muted-foreground text-sm flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-olive mt-2 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        )}
        {suitedFor && (
          <div className="mb-6">
            <h4 className="font-heading text-lg font-semibold text-foreground mb-3">Best Suited For</h4>
            <ul className="space-y-2">
              {suitedFor.map((s) => (
                <li key={s} className="text-muted-foreground text-sm flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Supply Formats:</span> {formats}</p>
      </div>
    </div>
  </section>
);

const Products = () => {
  return (
    <div>
      <PageBanner
        title="Our Olive Oil Profiles"
        subtitle="MM2A Tunisian Export supplies Tunisian olive oil profiles selected according to buyer expectations, intended use and market requirements."
        backgroundImage={heroImage}
      />

      <section className="section-padding">
        <div className="container-max text-center max-w-3xl mx-auto">
          <p className="text-muted-foreground leading-relaxed">
            Our commercial focus is bulk supply for professional buyers, not generic retail positioning. We mainly work with four product categories: Chemlali, Chetoui, custom Chemlali/Chetoui blends, and Lampante for industrial use.
          </p>
        </div>
      </section>

      <ProductSection
        title="Chemlali"
        subheadline="Tunisia's most widely cultivated variety — smooth, stable and commercially versatile."
        text={[
          "Chemlali is one of the most important olive oil varieties in Tunisia and represents a major part of the country's production base.",
          "It is appreciated for its smoother structure, broad usability and strong commercial flexibility across different market segments.",
          "This profile is particularly relevant for buyers looking for a scalable, versatile and market-friendly sourcing option.",
        ]}
        image={chemlaliImg}
        profileItems={["Smooth and balanced character", "Mild fruitiness", "Lower bitterness and pungency", "Stable profile across seasons", "Suitable for extra virgin classification depending on harvest and processing conditions"]}
        suitedFor={["Industrial buyers", "Food manufacturers", "Commercial bottlers", "Private label operations", "Buyers seeking blending flexibility"]}
        formats="IBC 1000L | Flexitank 22T | Tanker Truck | Other formats on request"
      />

      <ProductSection
        title="Chetoui"
        subheadline="Tunisia's high-intensity variety — greener, stronger and more distinctive."
        text={[
          "Chetoui is a more expressive Tunisian olive oil variety known for its stronger character, greener profile and more structured sensory identity.",
          "It is often chosen by buyers looking for more intensity, stronger origin expression and a product with more personality.",
          "This profile is especially relevant for premium positioning, high-character blends and buyers seeking a more differentiated olive oil offer.",
        ]}
        image={chetouiImg}
        profileItems={["Intense green character", "Stronger bitterness and pungency", "Higher polyphenol potential", "More structured sensory profile", "Strong Tunisian regional identity"]}
        suitedFor={["Premium blends", "Specialty importers", "High-end distributors", "Buyers focused on flavor intensity", "Markets valuing origin and product identity"]}
        formats="IBC 1000L | Flexitank 22T | Tanker Truck | Other formats on request"
        reversed
      />

      <ProductSection
        title="Custom Blends"
        subheadline="Tailored blending built around your exact commercial target."
        text={[
          "In addition to monovarietal sourcing, MM2A also develops custom blends combining Chemlali and Chetoui according to buyer requirements.",
          "This allows us to create more commercially adapted profiles by balancing softness, intensity, structure and target price positioning.",
          "Our blending approach is practical and buyer-oriented. We do not position blends as generic products, but as solutions built around specific commercial objectives.",
        ]}
        image={customBlendsImg}
        suitedFor={["Buyers needing a specific sensory balance", "Commercial bottlers", "Importers with target market requirements", "Buyers balancing quality and price positioning"]}
        formats="IBC 1000L | Flexitank 22T | Tanker Truck | Other formats on request"
      />

      <ProductSection
        title="Lampante"
        subheadline="Industrial-grade olive oil for refining and processing operations."
        text={[
          "MM2A also supplies Lampante olive oil in bulk for professional industrial use.",
          "Lampante is intended for refining and industrial processing rather than direct consumption. It is supplied according to buyer specifications, required volume and export logistics.",
          "This category is relevant for refiners, processors and industrial operators integrating olive oil into refining or large-scale blending operations.",
        ]}
        image={lampanteImg}
        suitedFor={["Refineries", "Industrial processors", "Bulk oil traders", "Buyers involved in refining operations"]}
        formats="IBC 1000L | Flexitank 22T | Tanker Truck | Bulk Tanker"
        reversed
      />

      {/* Commercial Flexibility */}
      <section className="bg-primary text-primary-foreground section-padding">
        <div className="container-max text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Commercial Flexibility</h2>
          <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Supply conditions, product selection, blend possibilities and logistics depend on buyer requirements, destination market and intended use. For this reason, we prefer direct commercial discussion in order to identify the most suitable sourcing solution.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gold text-charcoal hover:bg-gold-light font-semibold px-10">
              Request Product Information
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;
